/**
 * Global keyboard shortcuts. Keep declarations close to App.tsx so the surface
 * stays auditable; the hook is generic and tracks chord state internally.
 *
 * Default bindings:
 *   /        → focus search box (`#global-search-input`)
 *   n        → open "+ Add Computer" modal
 *   g d      → navigate to /devices  (chord)
 *   g w      → navigate to /work-orders (chord)
 *   esc      → close modals & drawer
 */

import { useEffect, useRef } from "react";

export interface ShortcutHandlers {
  focusSearch: () => void;
  openPair: () => void;
  goDevices: () => void;
  goWorkOrders: () => void;
  closeOverlay: () => void;
}

const TYPING_SELECTOR = "input,textarea,select,[contenteditable=true],[contenteditable='']";

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.matches(TYPING_SELECTOR);
}

export function useShortcuts(h: ShortcutHandlers): void {
  const chordRef = useRef<{ key: string; expires: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc always closes.
      if (e.key === "Escape") {
        h.closeOverlay();
        return;
      }

      if (isTyping(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Active chord — second key dispatches.
      const chord = chordRef.current;
      if (chord && Date.now() < chord.expires) {
        chordRef.current = null;
        if (chord.key === "g" && e.key === "d") {
          e.preventDefault();
          h.goDevices();
          return;
        }
        if (chord.key === "g" && e.key === "w") {
          e.preventDefault();
          h.goWorkOrders();
          return;
        }
      }

      switch (e.key) {
        case "/": {
          e.preventDefault();
          h.focusSearch();
          return;
        }
        case "n": {
          e.preventDefault();
          h.openPair();
          return;
        }
        case "g": {
          chordRef.current = { key: "g", expires: Date.now() + 1000 };
          return;
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [h]);
}
